import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FC } from "react";
import { IconInfoCircle } from "src/constants/icons";
import LoaderIconImage from "src/ui/components/BlockingLoadingOverlay/LoadingIconSmall.webp";
import Translate from "src/ui/components/Translate/Translate";
import NotificationCardCubit, { Variant } from "./NotificationCardCubit";

const Card = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 0.95fr auto;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  overflow: hidden;

  --card-bg: var(--color-cream-darker);
  --card-border: rgba(170, 167, 160, 0.5);
  --card-text: inherit;

  &[data-variant="waitingForHIE"] {
    --card-bg: rgba(95, 31, 143, 0.12);
    --card-border: rgba(95, 31, 143, 0.24);
    --card-text: var(--Accessible-PurpleHaze, #5f1f8f);
  }

  background: var(--card-bg);
  border: 1px solid var(--card-border);
  color: var(--card-text);
`;

const SpinKeyframes = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.img`
  width: 24px;
  height: 24px;
  animation: ${SpinKeyframes} 1.2s linear infinite;
  flex-shrink: 0;
`;

const IconWrap = styled.div`
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Content = styled.div`
  display: flex;
  gap: 8px;
`;

const cardTextBase = `
  font-family: "STK Bureau";
  font-size: 14px;
  font-style: normal;
  line-height: 140%;
  letter-spacing: -0.28px;
  font-feature-settings: 'liga' off, 'clig' off;
`;

const TextBlock = styled.div`
  flex: 1;
`;

const Title = styled.span`
  ${cardTextBase}
  font-weight: 500;
`;

const Description = styled.span`
  ${cardTextBase}
  font-weight: 400;
`;

const ImageContainer = styled.div`
  width: 4rem;
  height: 4em;
  margin-right: -1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &[data-custom="true"] {
    width: 6rem;
    height: 6em;
    margin: -1.75rem calc(-2rem - 10px) 0 0;
  }
`;

const VariantImage: FC<{
  variantImage: string;
  variantImageIsWide: boolean;
}> = ({ variantImage, variantImageIsWide }) => {
  return (
    <ImageContainer data-custom={variantImageIsWide}>
      <img src={variantImage} alt="" aria-hidden="true" />
    </ImageContainer>
  );
};

const NotificationCard: FC = () => {
  const [{ variant, variantImage, variantImageIsWide, isVisible }] =
    useBlocNext(NotificationCardCubit);

  if (!isVisible || !variant) {
    return null;
  }

  return (
    <Card
      role="article"
      aria-labelledby="notification.card.title"
      aria-describedby="notification.card.description"
      data-variant={variant}
    >
      <Content>
        <IconWrap aria-hidden="true">
          {variant === Variant.waitingForHIE ? (
            <Spinner src={LoaderIconImage} alt="" />
          ) : (
            <IconInfoCircle />
          )}
        </IconWrap>
        <TextBlock>
          <Title id="notification.card.title">
            <Translate
              msg="notification.card.title"
              variables={{ context: variant }}
            />
          </Title>
          <Description> - </Description>
          <Description id="notification.card.description">
            <Translate
              msg="notification.card.description"
              variables={{ context: variant }}
            />
          </Description>
        </TextBlock>
      </Content>
      {variantImage && (
        <VariantImage
          variantImage={variantImage}
          variantImageIsWide={variantImageIsWide}
        />
      )}
    </Card>
  );
};

export default NotificationCard;
